// File: src/layouts/MainLayout.jsx
// Description: Modern, professional layout with enhanced navigation, styling, and user experience.

import React, { useContext, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Avatar, 
  Menu, 
  MenuItem, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  useTheme,
  useMediaQuery,
  Container,
  Badge,
  Chip,
  Stack
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import logo from '../assets/touchconnect_logo1.png';
import FeedbackWidget from '../components/FeedbackWidget';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SmartToy from '@mui/icons-material/SmartToy';
import TrendingUp from '@mui/icons-material/TrendingUp';
import ParkIcon from '@mui/icons-material/Park';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

const MainLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDev = process.env.NODE_ENV === 'development';
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Navigation items
  const navigationItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/mentor-park', label: 'Mentor Park', icon: <ParkIcon /> },
    { path: '/conversations', label: 'Conversations', icon: <RecordVoiceOverIcon /> },
    { path: '/contacts', label: 'Contacts', icon: <PeopleIcon /> },
    { path: '/saved-profiles', label: 'Saved Profiles', icon: <PeopleIcon /> },
    { path: '/profile/qr', label: 'QR & NFC', icon: <QrCodeIcon /> },
    { path: '/ai-networking-assistant', label: 'AI Assistant', icon: <SmartToy /> },
    // Additional items not in the primary sequence
    { path: '/profile/edit', label: 'Edit Profile', icon: <PersonIcon /> },
    ...(isDev ? [{ path: '/ai-test', label: 'AI Test', icon: <SmartToy /> }] : []),
    { path: '/ai-profile-personalization', label: 'AI Profile Personalization', icon: <SmartToy /> },
  ];

  // Limit top bar (desktop) navigation to primary flows
  const topNavAllowed = new Set([
    '/dashboard',
    '/mentor-park',
    '/conversations',
    '/profile/qr',
    '/contacts',
    '/saved-profiles',
    '/ai-networking-assistant',
  ]);
  const topNavItems = navigationItems.filter(item => topNavAllowed.has(item.path));

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle view profile
  const handleViewProfile = () => {
    if (user?.public_slug) {
      const profileUrl = `${window.location.origin}/u/${user.public_slug}`;
      window.open(profileUrl, '_blank');
    }
  };

  // Mobile drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // User menu handlers
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Check if current path is active
  const isActivePath = (path) => location.pathname === path;

  // Shared nav button styles
  const navButtonBaseSx = {
    justifyContent: 'flex-start',
    borderRadius: 2,
    py: 1.25,
    px: 2,
    fontWeight: 500,
    transition: 'all 0.2s ease-in-out',
    '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 20 },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  };

  const getActiveSx = {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: 'white',
    boxShadow: '0 6px 16px rgba(30, 64, 175, 0.24)',
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
    }
  };

  const getInactiveHover = {
    '&:hover': {
      backgroundColor: theme.palette.primary[50],
      color: theme.palette.primary.main
    }
  };

  const getDesktopButtonSx = (path) => ({
    borderRadius: 2,
    px: 2,
    py: 1,
    fontWeight: 500,
    transition: 'all 0.2s ease-in-out',
    '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 20 },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    },
    ...(isActivePath(path) ? getActiveSx : getInactiveHover)
  });

  // Mobile drawer content
  const drawer = (
    <Box sx={{ 
      width: 320, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        p: 2, 
        textAlign: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        flexShrink: 0
      }}>
        <Box
          component="img"
          src={logo}
          alt="Touch Connect Logo"
          sx={{
            width: '64px',
            height: 'auto',
            mb: 1,
            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))'
          }}
        />
        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
          Touch Connect
        </Typography>
      </Box>

      {/* User Profile Section */}
      {user && (
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={user.profile_picture}
              sx={{ 
                width: 40, 
                height: 40, 
                mr: 2,
                border: `2px solid ${theme.palette.primary[200]}`
              }}
            >
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user.name || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email || 'user@example.com'}
              </Typography>
            </Box>
          </Box>
          
          <Stack spacing={0.75}>
            <Button
              variant="text"
              startIcon={<EditIcon />}
              onClick={() => { navigate('/profile/edit'); handleDrawerToggle(); }}
              fullWidth
              size="small"
              sx={{ 
                borderRadius: 1.5,
                justifyContent: 'flex-start',
                px: 1,
                py: 0.75
              }}
            >
              Edit Profile
            </Button>
            
            <Button
              variant="text"
              startIcon={<VisibilityIcon />}
              onClick={() => { handleViewProfile(); handleDrawerToggle(); }}
              fullWidth
              size="small"
              sx={{ 
                borderRadius: 1.5,
                justifyContent: 'flex-start',
                px: 1,
                py: 0.75
              }}
            >
              View Public Profile
            </Button>
            {user?.is_admin && (
              <Button
                variant="outlined"
                startIcon={<TrendingUp />}
                onClick={() => { navigate('/admin'); handleDrawerToggle(); }}
                fullWidth
                size="small"
                sx={{ 
                  borderRadius: 2,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Admin Dashboard
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* Navigation Items - Scrollable */}
      <Box sx={{ 
        flex: 1, 
        p: 1.5, 
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.grey[100],
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.grey[400],
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: theme.palette.grey[500],
          },
        },
      }}>
        {/* Navigate Section */}
        <Typography variant="overline" sx={{ 
          color: 'text.secondary', 
          fontWeight: 600, 
          mb: 2, 
          display: 'block' 
        }}>
          Navigate
        </Typography>
        
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {/* Dashboard */}
          <Button
            variant={location.pathname === '/dashboard' ? 'contained' : 'text'}
            startIcon={<DashboardIcon />}
            onClick={() => { navigate('/dashboard'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
            aria-label={'Dashboard'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/dashboard' ? getActiveSx : getInactiveHover)
            }}
          >
            Dashboard
          </Button>
          
          {/* Mentor Park */}
          <Button
            variant={location.pathname.startsWith('/mentor-park') ? 'contained' : 'text'}
            startIcon={<ParkIcon />}
            onClick={() => { navigate('/mentor-park'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname.startsWith('/mentor-park') ? 'page' : undefined}
            aria-label={'Mentor Park'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname.startsWith('/mentor-park') ? getActiveSx : getInactiveHover)
            }}
          >
            Mentor Park
          </Button>

          {/* Conversations */}
          <Button
            variant={location.pathname === '/conversations' ? 'contained' : 'text'}
            startIcon={<RecordVoiceOverIcon />}
            onClick={() => { navigate('/conversations'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/conversations' ? 'page' : undefined}
            aria-label={'Conversations'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/conversations' ? getActiveSx : getInactiveHover)
            }}
          >
            Conversations
          </Button>

          {/* QR & NFC */}
          <Button
            variant={location.pathname === '/profile/qr' ? 'contained' : 'text'}
            startIcon={<QrCodeIcon />}
            onClick={() => { navigate('/profile/qr'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/profile/qr' ? 'page' : undefined}
            aria-label={'QR & NFC'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/profile/qr' ? getActiveSx : getInactiveHover)
            }}
          >
            QR & NFC
          </Button>
          
          {/* Contacts */}
          <Button
            variant={location.pathname === '/contacts' ? 'contained' : 'text'}
            startIcon={<PeopleIcon />}
            onClick={() => { navigate('/contacts'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/contacts' ? 'page' : undefined}
            aria-label={'Contacts'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/contacts' ? getActiveSx : getInactiveHover)
            }}
          >
            Contacts
          </Button>
          
          {/* Saved Profiles */}
          <Button
            variant={location.pathname === '/saved-profiles' ? 'contained' : 'text'}
            startIcon={<BookmarkIcon />}
            onClick={() => { navigate('/saved-profiles'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/saved-profiles' ? 'page' : undefined}
            aria-label={'Saved Profiles'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/saved-profiles' ? getActiveSx : getInactiveHover)
            }}
          >
            Saved Profiles
          </Button>

          {/* AI Assistant */}
          <Button
            variant={location.pathname === '/ai-networking-assistant' ? 'contained' : 'text'}
            startIcon={<SmartToy />}
            onClick={() => { navigate('/ai-networking-assistant'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/ai-networking-assistant' ? 'page' : undefined}
            aria-label={'AI Assistant'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/ai-networking-assistant' ? getActiveSx : getInactiveHover)
            }}
          >
            AI Assistant
          </Button>

          {/* Admin (if applicable) */}
          {user?.is_admin && (
            <Button
              variant={location.pathname === '/admin' ? 'contained' : 'text'}
              startIcon={<TrendingUp />}
              onClick={() => { navigate('/admin'); handleDrawerToggle(); }}
              fullWidth
              disableRipple
              aria-current={location.pathname === '/admin' ? 'page' : undefined}
              aria-label={'Admin'}
              sx={{
                ...navButtonBaseSx,
                ...(location.pathname === '/admin' ? getActiveSx : getInactiveHover)
              }}
            >
              Admin
            </Button>
          )}
        </Stack>

        {/* Quick Actions */}
        <Typography variant="overline" sx={{ 
          color: 'text.secondary', 
          fontWeight: 600, 
          mb: 1.5, 
          display: 'block' 
        }}>
          Quick Actions
        </Typography>
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          <Button
            variant="text"
            startIcon={<VisibilityIcon />}
            onClick={() => { handleViewProfile(); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-label={'View Public Profile'}
            sx={{
              ...navButtonBaseSx,
              ...getInactiveHover
            }}
          >
            View Public Profile
          </Button>
          <Button
            variant={location.pathname === '/profile/edit' ? 'contained' : 'text'}
            startIcon={<PersonIcon />}
            onClick={() => { navigate('/profile/edit'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/profile/edit' ? 'page' : undefined}
            aria-label={'Edit Profile'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/profile/edit' ? getActiveSx : getInactiveHover)
            }}
          >
            Edit Profile
          </Button>
        </Stack>

        {/* AI */}
        <Typography variant="overline" sx={{ 
          color: 'text.secondary', 
          fontWeight: 600, 
          mb: 1.5, 
          display: 'block' 
        }}>
          AI
        </Typography>
        
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {/* AI Test (development only) */}
          {isDev && (
            <Button
              variant={location.pathname === '/ai-test' ? 'contained' : 'text'}
              startIcon={<SmartToy />}
              onClick={() => { navigate('/ai-test'); handleDrawerToggle(); }}
              fullWidth
              disableRipple
              aria-current={location.pathname === '/ai-test' ? 'page' : undefined}
              aria-label={'AI Test'}
              sx={{
                ...navButtonBaseSx,
                ...(location.pathname === '/ai-test' ? getActiveSx : getInactiveHover)
              }}
            >
              AI Test
            </Button>
          )}

          {/* AI Profile Personalization */}
          <Button
            variant={location.pathname === '/ai-profile-personalization' ? 'contained' : 'text'}
            startIcon={<SmartToy />}
            onClick={() => { navigate('/ai-profile-personalization'); handleDrawerToggle(); }}
            fullWidth
            disableRipple
            aria-current={location.pathname === '/ai-profile-personalization' ? 'page' : undefined}
            aria-label={'AI Profile Personalization'}
            sx={{
              ...navButtonBaseSx,
              ...(location.pathname === '/ai-profile-personalization' ? getActiveSx : getInactiveHover)
            }}
          >
            AI Profile Personalization
          </Button>
        </Stack>
      </Box>

      {/* Logout Section */}
      {user && (
        <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            variant="text"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            fullWidth
            size="small"
            sx={{ 
              borderRadius: 1.5,
              justifyContent: 'flex-start',
              px: 1,
              py: 0.75
            }}
          >
            Logout
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {/* App Bar */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 1, md: 0 } }}>
            {/* Logo and Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src={logo}
                    alt="Touch Connect Logo"
                    sx={{
                      width: { xs: '32px', md: '40px' },
                      height: 'auto',
                      mr: 2
                    }}
                  />
                  <Typography 
                    variant="h6" 
                    component="span" 
                    sx={{ 
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    Touch Connect
                  </Typography>
                </Box>
              </Link>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {topNavItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    startIcon={item.icon}
                    aria-current={isActivePath(item.path) ? 'page' : undefined}
                    aria-label={item.label}
                    disableRipple
                    sx={getDesktopButtonSx(item.path)}
                  >
                    {item.label}
                  </Button>
                ))}
                {user?.is_admin && (
                  <Button
                    component={Link}
                    to="/admin"
                    startIcon={<TrendingUp />}
                    aria-label={'Admin'}
                    disableRipple
                    sx={getDesktopButtonSx('/admin')}
                  >
                    Admin
                  </Button>
                )}
              </Box>
            )}

            {/* Right Side Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user ? (
                <>
                  {/* Notifications */}
                  <IconButton
                    size="large"
                    sx={{ 
                      color: theme.palette.text.secondary,
                      '&:hover': { color: theme.palette.primary.main }
                    }}
                  >
                    <Badge badgeContent={4} color="error">
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>

                  {/* User Avatar & Menu */}
                  <IconButton
                    onClick={handleMenuClick}
                    sx={{ 
                      p: 0,
                      '&:hover': { transform: 'scale(1.05)' },
                      transition: 'transform 0.2s ease-in-out'
                    }}
                  >
                    <Avatar
                      src={user?.profile_picture}
                      sx={{ 
                        width: 40, 
                        height: 40,
                        border: `2px solid rgba(255, 255, 255, 0.3)`,
                        '&:hover': { border: `2px solid rgba(255, 255, 255, 0.6)` }
                      }}
                    >
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: `1px solid ${theme.palette.divider}`
                      }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    {/* User Info */}
                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {user?.name || 'User'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user?.email || 'user@example.com'}
                      </Typography>
                    </Box>

                    {/* Menu Items */}
                    <Box sx={{ py: 1 }}>
                      <MenuItem onClick={() => { handleMenuClose(); handleViewProfile(); }}>
                        <ListItemIcon>
                          <VisibilityIcon fontSize="small" />
                        </ListItemIcon>
                        View Public Profile
                      </MenuItem>
                      <MenuItem onClick={() => { handleMenuClose(); navigate('/profile/edit'); }}>
                        <ListItemIcon>
                          <EditIcon fontSize="small" />
                        </ListItemIcon>
                        Edit Profile
                      </MenuItem>
                      <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        Logout
                      </MenuItem>
                    </Box>

                    {/* Divider */}
                    <Divider />

                    {/* Logout */}
                    <Box sx={{ py: 1 }}>
                      <MenuItem 
                        onClick={() => { handleMenuClose(); handleLogout(); }}
                        sx={{ 
                          color: theme.palette.error.main,
                          '&:hover': { backgroundColor: theme.palette.error[50] }
                        }}
                      >
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                        </ListItemIcon>
                        Logout
                      </MenuItem>
                    </Box>
                  </Menu>
                </>
              ) : (
                <>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/login"
                    sx={{ 
                      mr: 1,
                      color: theme.palette.text.secondary,
                      '&:hover': { color: theme.palette.primary.main }
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="gradient"
                    component={Link}
                    to="/register"
                    sx={{ 
                      borderRadius: 2,
                      px: 3,
                      py: 1
                    }}
                  >
                    Get Started
                  </Button>
                </>
              )}

              {/* Mobile Menu Button */}
              {isMobile && user && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ ml: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      {isMobile && user && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 320,
              border: 'none',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              maxWidth: '90vw'
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          minHeight: 'calc(100vh - 64px)',
          background: theme.palette.background.default,
          py: { xs: 2, md: 4 }
        }}
      >
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          mt: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" gutterBottom>
            &copy; {new Date().getFullYear()} Touch Connect. All rights reserved.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Built with ❤️ for modern networking
          </Typography>
        </Container>
      </Box>
      {/* Floating feedback button on all pages when logged in */}
      {user && <FeedbackWidget />}
    </>
  );
};

export default MainLayout;
